(() => {
  'use strict';
  const legacyStorageKey = 'feinwaelder-unverbindliche-vorbestellung-v1';
  const params = new URLSearchParams(location.search);
  const money = new Intl.NumberFormat(document.documentElement.lang || 'de-DE', { style: 'currency', currency: 'EUR' });

  // This publishes a consented lead signal, never a purchase or a delivery receipt.
  // The existing native Google tag receives generate_lead only after its API is ready.
  function reservationAnalytics(group, successful, productCatalog = new Map()) {
    const key = `feinwaelder-reservation-analytics-v1-${group}`;
    const maxAge = 30 * 60 * 1000;
    let finished = false;
    let retries = 0;
    let timer;
    let cancelGoogleLead = () => {};
    const allowed = () => {
      try { return window.Shopify?.customerPrivacy?.analyticsProcessingAllowed() === true; }
      catch (_) { return false; }
    };
    const clear = () => {
      try { sessionStorage.removeItem(key); return true; } catch (_) { return false; }
    };
    const validItems = items => Array.isArray(items) && items.length > 0 && items.length <= 56 &&
      items.every(item => item && typeof item.item_id === 'string' &&
        /^[a-z0-9][a-z0-9_-]{0,127}$/i.test(item.item_id) &&
        Number.isInteger(item.quantity) && item.quantity >= 1 && item.quantity <= 999) &&
      new Set(items.map(item => item.item_id)).size === items.length;
    function sendGoogleLead(payload) {
      const measurementId = 'G-2R90L772DC';
      const deadline = Date.now() + 5000;
      let stopped = false, clientReady = false, sessionReady = false, googleTimer;
      const stop = () => { stopped = true; clearTimeout(googleTimer); };
      cancelGoogleLead = stop;
      const active = () => {
        if (stopped) return false;
        if (!allowed() || Date.now() >= deadline) { stop(); return false; }
        return true;
      };
      const ready = field => value => {
        // IDs prove native readiness only; never retain, log or override them.
        if (!active() || !((typeof value === 'string' && value.trim()) ||
            (typeof value === 'number' && Number.isFinite(value) && value > 0))) return;
        if (field === 'client_id') clientReady = true;
        else sessionReady = true;
        if (!clientReady || !sessionReady) return;
        stop(); // Consume before calling: even repeated callbacks cannot duplicate it.
        try {
          window.gtag('event', 'generate_lead', {
            send_to: measurementId, event_id: payload.event_id,
            lead_source: 'product_reservation', reservation_group: payload.reservation_group,
            item_count: payload.item_count, package_count: payload.package_count,
            page_location: 'https://feinwaelder.de/collections/all', page_referrer: '',
            page_title: 'Feinwälder Vormerkung',
            allow_google_signals: false, allow_ad_personalization_signals: false
          });
        } catch (_) { /* A tag failure must never interrupt the reservation. */ }
        // Product rows are diagnostic events, not additional lead conversions.
        // Names come only from Shopify's current public catalog, never storage
        // or contact fields. Keep the original lead independent of catalog data.
        payload.items.forEach((item, index) => {
          if (!allowed() || item.item_id.length > 100 || !productCatalog.has(item.item_id)) return;
          const productName = productCatalog.get(item.item_id);
          if (typeof productName !== 'string' || !productName.trim()) return;
          try {
            window.gtag('event', 'reservation_item', {
              send_to: measurementId, event_id: `${payload.event_id}-${index + 1}`,
              lead_source: 'product_reservation', reservation_group: payload.reservation_group,
              reservation_product_id: item.item_id,
              reservation_product_name: Array.from(productName.trim()).slice(0, 100).join(''),
              reservation_quantity: item.quantity,
              page_location: 'https://feinwaelder.de/collections/all', page_referrer: '',
              page_title: 'Feinwälder Vormerkung',
              allow_google_signals: false, allow_ad_personalization_signals: false
            });
          } catch (_) { /* Each product is independent; never retry a sent row. */ }
        });
      };
      function check() {
        if (!active()) return;
        // Reuse the native tag. Never create/configure a tag or its dataLayer.
        if (typeof window.gtag === 'function') {
          try {
            window.gtag('get', measurementId, 'client_id', ready('client_id'));
            if (active()) window.gtag('get', measurementId, 'session_id', ready('session_id'));
          } catch (_) { /* Readiness can be retried only within this short window. */ }
        }
        if (active()) googleTimer = setTimeout(check, 250);
      }
      check();
    }
    function publishConfirmed() {
      if (finished) return;
      if (!allowed()) { clear(); return; }
      let pending;
      try { pending = JSON.parse(sessionStorage.getItem(key) || 'null'); } catch (_) { clear(); return; }
      const age = Date.now() - pending?.at;
      if (!pending || pending.version !== 1 || pending.group !== group ||
          !Number.isFinite(age) || age < 0 || age > maxAge ||
          typeof pending.event_id !== 'string' || !/^[a-z0-9-]{8,80}$/i.test(pending.event_id) ||
          !validItems(pending.items)) { clear(); finished = true; return; }
      const analytics = window.Shopify?.analytics;
      if (typeof analytics?.publish !== 'function') {
        // Shopify may initialize its event API after the form's success markup.
        if (!timer && retries++ < 12) timer = setTimeout(() => { timer = null; publishConfirmed(); }, 250);
        return;
      }
      // Consume before publishing: refresh, bfcache and repeat consent events must
      // not emit another lead. A failed publish is not retried as a new conversion.
      if (!clear()) return;
      finished = true;
      const items = pending.items.map(item => ({ item_id: item.item_id, quantity: item.quantity }));
      const payload = {
        event_id: pending.event_id, reservation_group: group, items,
        item_count: items.length,
        package_count: items.reduce((sum, item) => sum + item.quantity, 0)
      };
      try {
        Promise.resolve(analytics.publish('feinwaelder:reservation_submitted', payload)).catch(() => {});
      } catch (_) { /* Analytics must never interrupt the reservation flow. */ }
      sendGoogleLead(payload);
    }
    function privacyChanged() {
      if (!allowed()) { cancelGoogleLead(); clear(); return; }
      if (successful) publishConfirmed();
    }
    // A fresh form or validation-error page invalidates an older pending attempt.
    if (!successful) clear();
    document.addEventListener('visitorConsentCollected', privacyChanged);
    if (window.Shopify?.customerPrivacy) privacyChanged();
    else if (typeof window.Shopify?.loadFeatures === 'function') {
      try {
        window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], error => {
          if (!error) privacyChanged();
        });
      } catch (_) { /* Without a loaded privacy API no analytics is collected. */ }
    }
    return items => {
      if (!allowed()) { clear(); return; }
      const safeItems = items.map(item => ({ item_id: item.key, quantity: item.qty }));
      if (!validItems(safeItems)) { clear(); return; }
      const eventId = globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
      try {
        sessionStorage.setItem(key, JSON.stringify({
          version: 1, at: Date.now(), event_id: eventId, group, items: safeItems
        }));
      } catch (_) { /* Storage-disabled visits remain fully functional and untracked. */ }
    };
  }

  function deliveryWindow(now = new Date()) {
    const parts = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Berlin', year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(now);
    const value = type => Number(parts.find(p => p.type === type).value);
    const base = Date.UTC(value('year'), value('month') - 1, value('day'), 12);
    const format = new Intl.DateTimeFormat(document.documentElement.lang || 'de-DE', { timeZone: 'Europe/Berlin', day: '2-digit', month: '2-digit', year: 'numeric' });
    return [14, 21].map(days => format.format(new Date(base + days * 86400000)));
  }

  function enhanceConcepts() {
    const templates = new Map([...document.querySelectorAll('[data-fw-preorder-concept-template]')].map(el => [el.dataset.fwPreorderConceptTemplate, el]));
    const pages = [...document.querySelectorAll('[data-fw-concept-page]')];
    const pistachio = document.getElementById('fw-pistazien-title')?.closest('article');
    if (pistachio) pages.push(pistachio);
    for (const page of pages) {
      const key = page.dataset.fwConceptPage || 'dry-roasted-pistachios-salted-no-shell';
      const template = templates.get(key);
      if (!template || page.dataset.fwReservationEnhanced) continue;
      const target = page.querySelector('.fw-concept-size');
      if (!target) continue;
      page.dataset.fwReservationEnhanced = 'true';
      target.after(template.content.cloneNode(true));
      for (const details of page.querySelectorAll('details')) {
        // Do not show fictional design-example reviews on reservation pages.
        if (details.querySelector('.fw-demo-review')) details.remove();
      }
      for (const link of page.querySelectorAll('.fw-review-link')) {
        if (link.textContent.includes('Beispielbewertung')) link.remove();
      }
    }
    const adminPages = [...document.querySelectorAll('[data-fw-admin-page]')];
    if (adminPages.length) {
      const selected = adminPages.find(el => el.dataset.fwAdminPage === params.get('produkt')) || adminPages[0];
      adminPages.forEach(el => { el.hidden = el !== selected; });
      document.title = `${selected.querySelector('h1').textContent} | Feinwälder`;
    }
  }

  function initForm() {
    const root = document.querySelector('[data-fw-reservation-page]');
    if (!root || root.dataset.ready) return;
    root.dataset.ready = 'true';
    const group = root.dataset.fwReservationGroup === 'returning' ? 'returning' : 'new';
    const storageKey = `feinwaelder-unverbindliche-vorbestellung-v2-${group}`;
    const success = root.querySelector('[data-fw-reservation-success]');
    const analyticsCatalog = root.querySelector('[data-fw-reservation-analytics-catalog]');
    const productCatalog = new Map([...(analyticsCatalog?.content.querySelectorAll('option[data-key]') || [])]
      .filter(option => option.dataset.group === group && option.dataset.label)
      .map(option => [option.dataset.key, option.dataset.label]));
    const rememberAnalytics = reservationAnalytics(group, Boolean(success), productCatalog);
    if (success) {
      // An empty draft also prevents an old shared draft from being restored again.
      try { sessionStorage.setItem(storageKey, JSON.stringify({ at: Date.now(), items: [] })); } catch (_) {}
      success.focus();
      return;
    }
    const form = root.querySelector('#FWReservationForm');
    const rows = form?.querySelector('[data-fw-reservation-rows]');
    const firstRow = rows?.querySelector('[data-fw-reservation-row]');
    if (!form || !rows || !firstRow) return;
    const rowTemplate = firstRow.cloneNode(true);
    const catalogue = new Map([...firstRow.querySelector('select').options]
      .filter(option => option.dataset.key && option.dataset.group === group)
      .map(option => [option.dataset.key, Object.freeze({
        key: option.dataset.key, group, value: option.value,
        label: option.dataset.label || option.textContent.trim(), price: Number(option.dataset.price),
        unitPrice: (option.dataset.unitPrice || '').trim()
      })]));
    const add = form.querySelector('[data-fw-reservation-add]');
    const submit = form.querySelector('[data-fw-reservation-submit]');
    const submitLabel = submit.textContent;
    const summary = form.querySelector('[data-fw-reservation-summary]');
    const reference = form.querySelector('[data-fw-reservation-reference]');
    const error = form.querySelector('[data-fw-reservation-client-error]');
    const maxRows = Math.min(56, catalogue.size);
    const incomingKey = params.get('artikel');
    const alternate = root.querySelector('[data-fw-reservation-alternate-options]');
    const otherOption = alternate && [...alternate.content.querySelectorAll('option[data-key]')]
      .find(option => option.dataset.key === incomingKey && option.dataset.group !== group);

    // Old shared links must retain the clicked article when selecting the correct form.
    if (incomingKey && !catalogue.has(incomingKey) && otherOption) {
      const url = new URL(location.href);
      url.searchParams.set('view', otherOption.dataset.group === 'returning'
        ? 'feinwaelder-vorbestellen-vorteil' : 'feinwaelder-vorbestellen-neu');
      if (error) {
        const link = document.createElement('a');
        link.href = url.href;
        link.textContent = (document.documentElement.lang.toLowerCase().startsWith('fr') ? "Accéder à la page de réservation adaptée" : (document.documentElement.lang.toLowerCase().startsWith('it') ? "Vai alla pagina di prenotazione corretta" : 'Zur passenden Vormerkseite wechseln'));
        error.replaceChildren(document.createTextNode((document.documentElement.lang.toLowerCase().startsWith('fr') ? "L’article sélectionné appartient à l’autre groupe de réservation. " : (document.documentElement.lang.toLowerCase().startsWith('it') ? "L’articolo selezionato appartiene all’altra categoria di prenotazione. " : globalThis.fwExtraText("fw_extra.s_c1edfc4ec3d832", 'Dein gewählter Artikel gehört zur anderen Vormerkgruppe. ')))), link);
        error.hidden = false;
      }
      location.replace(url.href);
      return;
    }

    const nonce = globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    reference.value = `FW-${nonce}`;
    let submitting = false;
    let pendingSubmit = null;

    const readRows = () => [...rows.querySelectorAll('[data-fw-reservation-row]')].map(row => {
      const option = row.querySelector('select').selectedOptions[0];
      const item = catalogue.get(option?.dataset.key);
      const known = item && item.value === option.value && option.dataset.group === group;
      return { key: known ? item.key : '', qty: Number(row.querySelector('[data-fw-reservation-quantity]').value) };
    });
    const validQuantity = value => Number.isInteger(value) && value >= 1 && value <= 999;
    function save() {
      const items = readRows().filter(item => catalogue.has(item.key) && validQuantity(item.qty));
      try { sessionStorage.setItem(storageKey, JSON.stringify({ at: Date.now(), items })); } catch (_) {}
    }
    function showError(message) {
      if (!error) return;
      error.textContent = message;
      error.hidden = !message;
    }
    function clearValidity() {
      rows.querySelectorAll('select, input').forEach(input => input.setCustomValidity(''));
      showError('');
    }
    function update() {
      clearValidity();
      const window = deliveryWindow();
      const allRows = [...rows.querySelectorAll('[data-fw-reservation-row]')];
      allRows.forEach((row, index) => {
        const n = index + 1;
        const select = row.querySelector('select');
        const qty = row.querySelector('[data-fw-reservation-quantity]');
        row.querySelector('legend').textContent = (document.documentElement.lang.toLowerCase().startsWith('fr') ? `Article ${n}` : (document.documentElement.lang.toLowerCase().startsWith('it') ? `Articolo ${n}` : globalThis.fwExtraFormat("fw_extra.s_0efdc94bb5bc33", `Artikel ${n}`, [n])));
        select.id = `FWReservationProduct-${n}`; select.name = `contact[Artikel ${n}]`;
        qty.id = `FWReservationQty-${n}`; qty.name = `contact[Menge ${n}]`;
        row.querySelectorAll('label')[0].htmlFor = select.id;
        row.querySelectorAll('label')[1].htmlFor = qty.id;
        row.querySelector('[data-fw-reservation-remove]').hidden = allRows.length === 1;
        const item = catalogue.get(select.selectedOptions[0]?.dataset.key);
        const info = row.querySelector('[data-fw-reservation-info]');
        info.dataset.group = item?.group || '';
        if (!item) {
          info.textContent = (document.documentElement.lang.toLowerCase().startsWith('fr') ? "Choisis un produit. Le délai de livraison et la remise s’afficheront ici." : (document.documentElement.lang.toLowerCase().startsWith('it') ? "Scegli un prodotto. I tempi di consegna e lo sconto saranno visualizzati qui." : globalThis.fwExtraText("fw_extra.s_c459a12a18b69a", 'Bitte wähle ein Produkt. Lieferzeit und Rabatt werden dann hier angezeigt.')));
        } else if (item.group === 'new') {
          info.textContent = (document.documentElement.lang.toLowerCase().startsWith('fr') ? `Livraison estimée : 2 à 3 semaines après l’envoi de ta réservation (si elle est reçue aujourd’hui : du ${window[0]} au ${window[1]}). Aucune remise de précommande.` : (document.documentElement.lang.toLowerCase().startsWith('it') ? `Consegna prevista: 2–3 settimane dall’invio della prenotazione (se ricevuta oggi: dal ${window[0]} al ${window[1]}). Nessuno sconto sulla prenotazione.` : globalThis.fwExtraFormat("fw_extra.s_385e23650d83e7", `Voraussichtliche Lieferung: 2–3 Wochen ab Absenden deiner Vorbestellung (bei Eingang heute: ${window[0]} bis ${window[1]}). Kein Vorbesteller-Rabatt.`, [window[0], window[1]])));
        } else {
          info.textContent = (document.documentElement.lang.toLowerCase().startsWith('fr') ? "À nouveau disponible au plus tôt le 1er mars 2027. Remise de 15 % sur cet article réservé de l’assortiment existant lors de l’achat ultérieur." : (document.documentElement.lang.toLowerCase().startsWith('it') ? "Nuovamente disponibile non prima del 1° marzo 2027. Sconto del 15% su questo articolo prenotato dell’assortimento esistente al successivo acquisto." : globalThis.fwExtraText("fw_extra.s_b9660142a62411", 'Frühestens ab 1. März 2027 wieder erhältlich. 15 % Rabatt auf diesen vorgemerkten Bestandsartikel beim späteren Kauf.')));
        }
        if (item) {
          info.textContent += item.price > 0 ? (document.documentElement.lang.toLowerCase().startsWith('fr') ? ` Prix actuellement affiché : ${money.format(item.price / 100)} par paquet.${item.unitPrice ? ` Prix à l’unité de mesure : ${item.unitPrice}.` : ''} Aucun paiement aujourd’hui.` : (document.documentElement.lang.toLowerCase().startsWith('it') ? ` Prezzo attualmente indicato: ${money.format(item.price / 100)} per confezione.${item.unitPrice ? ` Prezzo per unità di misura: ${item.unitPrice}.` : ''} Oggi non paghi nulla.` : globalThis.fwExtraFormat("fw_extra.s_8f71cbc44168fb", ` Aktuell angezeigter Artikelpreis: ${money.format(item.price / 100)} pro Packung.${item.unitPrice ? ` Grundpreis: ${item.unitPrice}.` : ''} Heute keine Zahlung.`, [money.format(item.price / 100), item.unitPrice ? globalThis.fwExtraFormat("fw_extra.s_a1550729ea6bed", ` Grundpreis: ${item.unitPrice}.`, [item.unitPrice]) : '']))) : (document.documentElement.lang.toLowerCase().startsWith('fr') ? " Aucun paiement aujourd’hui." : (document.documentElement.lang.toLowerCase().startsWith('it') ? " Oggi non paghi nulla." : globalThis.fwExtraText("fw_extra.s_2883fc178903dc", ' Heute keine Zahlung.')));
        }
      });
      add.hidden = false; add.disabled = allRows.length >= maxRows;
      if (!catalogue.size) submit.disabled = true;
      save();
    }
    function appendItem(item = {}) {
      if (rows.children.length >= maxRows) return;
      const row = rowTemplate.cloneNode(true);
      row.querySelector('select').value = catalogue.get(item.key)?.value || '';
      row.querySelector('[data-fw-reservation-quantity]').value = validQuantity(item.qty) ? item.qty : 1;
      rows.append(row);
    }
    let saved = [];
    try {
      const raw = sessionStorage.getItem(storageKey) ?? sessionStorage.getItem(legacyStorageKey);
      const data = JSON.parse(raw || 'null');
      const age = Date.now() - data?.at;
      if (data && Number.isFinite(age) && age >= 0 && age < 7 * 86400000 && Array.isArray(data.items)) {
        const seen = new Set();
        saved = data.items.filter(item => {
          if (!item || !catalogue.has(item.key) || !validQuantity(item.qty) || seen.has(item.key)) return false;
          seen.add(item.key); return true;
        }).slice(0, maxRows);
      }
    } catch (_) {}
    if (catalogue.has(incomingKey)) {
      const existing = saved.find(item => item.key === incomingKey);
      const qty = params.has('menge') || !existing ? Number(params.get('menge') || 1) : existing.qty;
      saved = [{ key: incomingKey, qty: validQuantity(qty) ? qty : 1 }, ...saved.filter(item => item.key !== incomingKey)];
    }
    if (saved.length) { rows.replaceChildren(); saved.slice(0, maxRows).forEach(appendItem); }
    update();
    if (incomingKey && !catalogue.has(incomingKey)) {
      showError((document.documentElement.lang.toLowerCase().startsWith('fr') ? "L’article de ton lien ne peut pas être réservé ici. Vérifie ta sélection." : (document.documentElement.lang.toLowerCase().startsWith('it') ? "L’articolo del tuo link non può essere prenotato qui. Controlla la selezione." : globalThis.fwExtraText("fw_extra.s_a8da6b3e51c562", 'Der Artikel aus deinem Link ist hier nicht vormerkbar. Bitte prüfe deine Auswahl.'))));
    }
    add.addEventListener('click', () => { appendItem(); update(); rows.lastElementChild.querySelector('select').focus(); });
    rows.addEventListener('change', update);
    rows.addEventListener('input', e => {
      if (e.target.matches('[data-fw-reservation-quantity]')) { clearValidity(); save(); }
    });
    rows.addEventListener('click', e => {
      const remove = e.target instanceof Element && e.target.closest('[data-fw-reservation-remove]');
      if (!remove || rows.children.length < 2) return;
      remove.closest('[data-fw-reservation-row]').remove(); update(); add.focus();
    });
    form.addEventListener('submit', event => {
      if (submitting || (pendingSubmit && !pendingSubmit.defaultPrevented)) {
        event.preventDefault(); return;
      }
      clearValidity();
      const items = readRows();
      const seen = new Set();
      let message = '';
      [...rows.querySelectorAll('[data-fw-reservation-row]')].forEach((row, index) => {
        const item = items[index];
        const select = row.querySelector('select');
        const qty = row.querySelector('[data-fw-reservation-quantity]');
        if (!catalogue.has(item.key)) {
          select.setCustomValidity((document.documentElement.lang.toLowerCase().startsWith('fr') ? "Choisis un produit de ce groupe de réservation." : (document.documentElement.lang.toLowerCase().startsWith('it') ? "Scegli un prodotto di questa categoria di prenotazione." : globalThis.fwExtraText("fw_extra.s_4405fbbeb4ef8b", 'Bitte wähle ein Produkt aus dieser Vormerkgruppe.'))));
          message ||= select.validationMessage;
        } else if (seen.has(item.key)) {
          select.setCustomValidity((document.documentElement.lang.toLowerCase().startsWith('fr') ? "Ce produit est déjà sélectionné. Modifie la quantité sur la ligne existante." : (document.documentElement.lang.toLowerCase().startsWith('it') ? "Questo prodotto è già selezionato. Modifica la quantità nella riga esistente." : globalThis.fwExtraText("fw_extra.s_8596ff4585d856", 'Dieses Produkt ist bereits ausgewählt. Bitte ändere die Menge in der vorhandenen Zeile.'))));
          message ||= select.validationMessage;
        }
        seen.add(item.key);
        if (!validQuantity(item.qty)) {
          qty.setCustomValidity((document.documentElement.lang.toLowerCase().startsWith('fr') ? "Saisis un nombre entier compris entre 1 et 999 paquets." : (document.documentElement.lang.toLowerCase().startsWith('it') ? "Inserisci un numero intero compreso tra 1 e 999 confezioni." : globalThis.fwExtraText("fw_extra.s_ffb0aa5650c28d", 'Bitte gib eine ganze Menge zwischen 1 und 999 Packungen ein.'))));
          message ||= qty.validationMessage;
        }
      });
      if (!items.length || items.length > maxRows) message ||= (document.documentElement.lang.toLowerCase().startsWith('fr') ? "Vérifie les articles sélectionnés." : (document.documentElement.lang.toLowerCase().startsWith('it') ? "Controlla gli articoli selezionati." : globalThis.fwExtraText("fw_extra.s_5f5215f615c791", 'Bitte prüfe die ausgewählten Artikel.')));
      if (!form.reportValidity() || message) {
        event.preventDefault(); showError(message || (document.documentElement.lang.toLowerCase().startsWith('fr') ? "Vérifie les champs obligatoires et confirme la réservation sans engagement." : (document.documentElement.lang.toLowerCase().startsWith('it') ? "Controlla i campi obbligatori e conferma la prenotazione senza impegno." : globalThis.fwExtraText("fw_extra.s_83e6e3acee6b99", 'Bitte prüfe die Pflichtfelder und bestätige die unverbindliche Vormerkung.'))));
        return;
      }
      const now = new Date(); const dates = deliveryWindow(now);
      form.querySelector('[data-fw-reservation-time]').value = now.toISOString();
      const lines = ['FEINWAELDER_VORMERKUNG_V1', `Referenz: ${reference.value}`, 'Kostenlose, unverbindliche Vormerkung. Keine Zahlung, noch kein Kaufvertrag.', ''];
      items.forEach((item, index) => {
        const product = catalogue.get(item.key);
        lines.push(`${index + 1}. ${product.label} | ${item.qty} Packung(en) | ID: ${item.key}`);
        lines.push(group === 'new' ? `NEU | 0 % Rabatt | voraussichtliche Lieferung 2–3 Wochen nach Eingang, aktuell ${dates[0]} bis ${dates[1]}.` : 'BESTAND | 15 % Rabatt beim späteren Kauf | frühestens ab 01.03.2027.');
      });
      lines.push('', 'Rabatt nur auf die vorgemerkten Bestandsartikel. Keine Rabatte auf neue Produkte, Natron oder keltisches Meersalz.', 'Verfügbarkeit und endgültige Angaben vor dem Kauf bestätigen. Anschließend passenden Kauflink senden.');
      summary.value = lines.join('\n');
      save(); pendingSubmit = event;
      // Shopify can prevent submission while completing its anti-spam challenge.
      // Lock only when the native submission is actually allowed to continue.
      queueMicrotask(() => {
        if (event.defaultPrevented) {
          if (pendingSubmit === event) pendingSubmit = null;
          return;
        }
        rememberAnalytics(items);
        submitting = true; submit.disabled = true;
        submit.setAttribute('aria-busy', 'true'); submit.textContent = (document.documentElement.lang.toLowerCase().startsWith('fr') ? "Envoi de la demande…" : (document.documentElement.lang.toLowerCase().startsWith('it') ? "Invio della richiesta…" : globalThis.fwExtraText("fw_extra.s_d4f9e33647e207", 'Anfrage wird übermittelt …')));
      });
    });
    window.addEventListener('pageshow', () => {
      submitting = false; pendingSubmit = null;
      submit.disabled = !catalogue.size;
      submit.removeAttribute('aria-busy'); submit.textContent = submitLabel;
    });
  }
  document.addEventListener('click', e => {
    const link = e.target.closest('[data-fw-preorder-link]');
    if (!link) return;
    const holder = link.closest('product-info, .product__info-container');
    const qty = Number(holder?.querySelector('input[name="quantity"]')?.value || 1);
    if (Number.isInteger(qty) && qty > 0 && qty <= 999) {
      const url = new URL(link.href, location.origin); url.searchParams.set('menge', String(qty)); link.href = url.href;
    }
  });
  function init() { enhanceConcepts(); initForm(); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true }); else init();
  document.addEventListener('shopify:section:load', init);
})();
